/**
 * MBA Lab — the team charter builder's preset option lists and their
 * document-facing text. Pure data, no functions: charter-model.js reads
 * this and the Razor view never hardcodes preset copy, matching the
 * planner-modes.js precedent of keeping every piece of editorial copy in
 * one place a copy-editor can find without touching the state-machine
 * logic in charter-model.js.
 */

export const AMBITION_KEYS = ['top-of-curve', 'strong-efficient', 'pass-deliberately'];
export const ROTATION_KEYS = ['fixed', 'per-module'];
export const CADENCE_KEYS = ['weekly', 'twice-weekly', 'as-needed'];
export const CHANNEL_KEYS = ['group-chat', 'email-thread', 'text-group'];
export const SLA_KEYS = ['24h-weekdays', '12h', 'same-day'];
export const BUFFER_KEYS = ['24h', '48h'];
export const DECISION_METHOD_KEYS = ['consensus-24h', 'majority-owner-ties', 'owner-decides-after-input', 'consensus-10min-cap'];
export const RETRO_KEYS = ['per-deliverable', 'mid-and-end', 'end-only'];
// v2 addition (see charter-model.js's MIGRATIONS) - closes the "workload
// signals" gap found when this charter was checked against the Leadership
// Lab's requirements (decision rights, response-time, conflict handling, and
// feedback cadence were already covered by existing fields; workload signals
// and an explicit repair process were not).
export const WORKLOAD_SIGNAL_KEYS = ['say-it-early', 'buddy-check-in', 'redistribute-openly'];

export const AMBITION_TEXT = {
  'top-of-curve': "We're aiming for the top of the curve - we'll push each other and put in the extra hours when it matters.",
  'strong-efficient': "We're aiming for strong, efficient work - solid grades without burning out the team.",
  'pass-deliberately': "We're prioritizing passing this course efficiently - our real focus is elsewhere this term.",
};

export const ROTATION_TEXT = {
  fixed: 'Roles stay fixed for the term unless the team agrees to change them.',
  'per-module': 'Roles rotate at the start of each module so everyone gets a turn.',
};

export const CADENCE_TEXT = {
  weekly: 'We meet weekly, same day and time.',
  'twice-weekly': 'We meet twice a week during crunch periods, weekly otherwise.',
  'as-needed': 'We meet as needed, scheduled at least 48 hours ahead.',
};

export const CHANNEL_TEXT = {
  'group-chat': 'Group chat (Slack, Teams, or WhatsApp) is our primary channel.',
  'email-thread': 'A shared email thread is our primary channel.',
  'text-group': 'A group text is our primary channel.',
};

export const SLA_TEXT = {
  '24h-weekdays': 'We respond within 24 hours on weekdays.',
  '12h': 'We respond within 12 hours.',
  'same-day': 'We respond the same day a message is sent.',
};

export const BUFFER_TEXT = {
  '24h': 'We build in a 24-hour buffer before the real deadline.',
  '48h': 'We build in a 48-hour buffer before the real deadline.',
};

export const DECISION_METHOD_TEXT = {
  'consensus-24h': "We default to consensus. If nobody objects within 24 hours of a proposal, it's approved.",
  'majority-owner-ties': "We decide by majority vote. If it's tied, the item's owner makes the final call.",
  'owner-decides-after-input': "The owner of each deliverable decides after hearing everyone's input - we don't need unanimous agreement to move forward.",
  'consensus-10min-cap': "We try for consensus, capped at 10 minutes of discussion - if we're still stuck, we vote.",
};

export const RETRO_TEXT = {
  'per-deliverable': 'We retro after every deliverable.',
  'mid-and-end': 'We retro at the midpoint and the end of the term.',
  'end-only': 'We retro once, at the end of the term.',
};

export const WORKLOAD_SIGNAL_TEXT = {
  'say-it-early': "If someone's at capacity, we say so directly at the next meeting - we don't wait for a deadline to reveal it.",
  'buddy-check-in': "We check in with each other proactively if someone's gone quiet or missed something small - not just after a deadline is blown.",
  'redistribute-openly': "If someone's overloaded, we talk about redistributing work openly, as a team decision, not a silent favor.",
};

export const PRESET_TEXT = {
  ambition: AMBITION_TEXT,
  rotation: ROTATION_TEXT,
  cadence: CADENCE_TEXT,
  channel: CHANNEL_TEXT,
  sla: SLA_TEXT,
  buffer: BUFFER_TEXT,
  decisionMethod: DECISION_METHOD_TEXT,
  retro: RETRO_TEXT,
  workloadSignal: WORKLOAD_SIGNAL_TEXT,
};
